import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useReactToPrint } from "react-to-print";
import { Box } from "@mui/material";
import { Add, Edit, Delete } from "@mui/icons-material";

import { PageLayout } from "../../layouts/PageLayout";
import { AddReturn, AddPerformanceGuarantees } from "../../components/performanceGuarantee";
import { CardLayout, Invoice, PageHeader, DeleteModal } from "../../components/v3";
import { DownloadAndPrint } from "../../components/DownloadAndPrint";
import { PerformanceGuaranteesService } from "../../services";
import { RELEASE_PERFORMANCE_GUARANTEE, EDIT_PERFORMANCE_GUARANTEE, DELETE_PERFORMANCE_GUARANTEE } from "../../constants";
import { useHasPermission } from "../../utils";
import { getDateAndTime } from "../../utils/formatters";
import { get_date } from "../../components/generalComponets";
import { PERFORMANCE_GUARANTEES_ROUTES } from "./constants";
import { RESTORE_TABLE_STATE } from "../../utils/tableStateStorage";

interface UserInfo {
  firstName: string;
  lastName: string;
}

interface PerformanceGuaranteeData {
  id?: string;
  name?: string;
  status?: string;
  releaseTransactionId?: string;
  depositAmount?: string | number;
  depositDate?: string;
  endDate?: string;
  releaseDate?: string;
  createdBy?: UserInfo;
  createdAt?: string;
  updatedBy?: UserInfo;
  updatedAt?: string;
}

export const SinglePerformanceGuarantees = () => {
  const [pgDetails, setPgDetails] = useState<Partial<PerformanceGuaranteeData> | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState<any>({ isOpen: false, pgId: null });
  const [reload, setReload] = useState(0);

  const hasApprovePG = useHasPermission(RELEASE_PERFORMANCE_GUARANTEE);
  const hasEditPG = useHasPermission(EDIT_PERFORMANCE_GUARANTEE);
  const hasDeletePG = useHasPermission(DELETE_PERFORMANCE_GUARANTEE);
  const { id } = useParams();
  const navigate = useNavigate();
  const printRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchData(id);
  }, [id, reload]);

  const fetchData = async (pgId) => {
    try {
      const { data: response } = await PerformanceGuaranteesService.getPerformanceGuaranteeById(pgId);
      setPgDetails(response);
    } catch (error) {
      console.error("Error fetching PG details:", error);
      toast.error("Failed to fetch PG details.");
    }
  };

  const handleOpenModal = () => setOpenModal(true);
  const handleCloseModal = () => {
    setOpenModal(false);
    setOpenEditModal(false);
  };

  const handleDelete = async () => {
    try {
      await PerformanceGuaranteesService.deletePerformanceGuarantee(deleteModal.pgId);
      toast.success("Performance Guarantee deleted successfully");
      setDeleteModal({ isOpen: false, pgId: null });
      // Back on the list with the filters it had, like the browser's Back button.
      navigate(PERFORMANCE_GUARANTEES_ROUTES.root, { state: RESTORE_TABLE_STATE });
    } catch (error) {
      toast.error("Failed to delete Performance Guarantee");
      console.error("Error deleting PG:", error);
    }
  };

  const handlePrint = useReactToPrint({
    contentRef: printRef,
  });

  const handleReload = () => setReload((prev) => prev + 1);

  const buttons = [
    hasApprovePG && {
      buttonText: "Add Return",
      buttonIcon: Add,
      onClick: handleOpenModal,
      disabled: !!pgDetails?.releaseTransactionId,
    },
    hasEditPG &&
      pgDetails?.status === "ACTIVE" && {
        buttonText: "Edit",
        buttonIcon: Edit,
        onClick: () => setOpenEditModal(true),
      },
    hasDeletePG &&
      pgDetails?.status === "ACTIVE" && {
        buttonText: "Delete",
        buttonIcon: Delete,
        onClick: () => setDeleteModal({ isOpen: true, pgId: pgDetails?.id }),
      },
  ].filter(Boolean);

  const rows = [
    {
      name: pgDetails?.name || "-",
      depositAmount: pgDetails?.depositAmount ? `₹ ${pgDetails?.depositAmount}` : "-",
      depositDate: pgDetails?.depositDate ? get_date(pgDetails?.depositDate) : "-",
      endDate: pgDetails?.endDate ? get_date(pgDetails?.endDate) : "-",
      releasedDate: pgDetails?.releaseDate ? get_date(pgDetails?.releaseDate) : "-",
    },
  ];

  return (
    <>
      <PageLayout>
        {/* header section */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <PageHeader title={pgDetails?.name} buttons={buttons} />
        </Box>

        {/* table section */}
        <Box sx={{ mt: -9, zIndex: 2, position: "relative" }}>
          <CardLayout>
            <div ref={printRef}>
              <Invoice
                title="Fixed Deposit"
                sectionB={[
                  {
                    label: "Created By",
                    value: pgDetails?.createdBy
                      ? `${pgDetails?.createdBy?.firstName} ${pgDetails?.createdBy?.lastName} (${getDateAndTime(
                          pgDetails?.createdAt,
                        )})`
                      : "N/A",
                  },
                  pgDetails?.updatedBy && {
                    label: "Last Updated By",
                    value: `${pgDetails?.updatedBy?.firstName} ${pgDetails?.updatedBy?.lastName} (${getDateAndTime(
                      pgDetails?.updatedAt,
                    )})`,
                  },
                ].filter(Boolean)}
                sectionC={[{ label: "Name", value: pgDetails?.name }]}
                sectionD={[{ label: "Amount", value: `₹ ${pgDetails?.depositAmount}` }]}
                columns={[
                  { id: "name", label: "Name" },
                  { id: "depositAmount", label: "Amount" },
                  { id: "depositDate", label: "Deposit Date" },
                  { id: "endDate", label: "End Date" },
                  { id: "releasedDate", label: "Released Date" },
                ]}
                rows={rows}
              />
            </div>
          </CardLayout>
        </Box>

        <DownloadAndPrint handlePrint={handlePrint} printRef={printRef} fileName={"SinglePerformanceGuarantees.pdf"} />
      </PageLayout>

      {/* approve pg modal */}
      <AddReturn pgData={pgDetails} open={openModal} onClose={handleCloseModal} onSuccess={handleReload} />

      {/* edit pg modal */}
      <AddPerformanceGuarantees
        open={openEditModal}
        onClose={() => handleCloseModal()}
        onSuccess={handleReload}
        pgData={pgDetails}
      />

      {/* delete pg modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={() => setDeleteModal({ isOpen: false, pgId: null })}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this Performance Guarantee?"
      />
    </>
  );
};
